import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { API, formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, ArrowLeft, Download, FileCheck2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function BulkUpload({ kind = "patients" }) {
  const isPatients = kind === "patients";
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const nav = useNavigate();

  const submit = async () => {
    if (!file) { toast.error("Pick a file first"); return; }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = isPatients ? "/patients/bulk-upload" : "/medicines/bulk-upload";
      const { data } = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if ((data?.failed || []).length === 0) {
        toast.success(`Imported ${data.inserted} record(s)`);
      } else {
        toast.warning(`Imported ${data.inserted}, ${data.failed.length} failed`);
      }
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => {
    const path = isPatients ? "/patients/sample-template" : "/medicines/sample-template";
    window.open(`${API}${path}`, "_blank");
  };

  return (
    <div className="p-6 md:p-8 max-w-3xl" data-testid={`bulk-upload-${kind}-page`}>
      <Button asChild variant="ghost" className="mb-3">
        <Link to={isPatients ? "/patients" : "/medicines"}><ArrowLeft size={14} className="mr-2" /> Back</Link>
      </Button>
      <h1 className="text-3xl font-semibold text-slate-900 tracking-tight mb-1" style={{ fontFamily: "Work Sans" }}>
        Bulk Upload {isPatients ? "Patients" : "Medicines"}
      </h1>
      <p className="text-slate-500 text-sm mb-6">CSV or XLSX accepted. Required column: <code>{isPatients ? "full_name" : "medicine_name"}</code>.</p>

      <Card className="p-5 border-slate-200 mb-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-medium text-slate-900">1. Download template</div>
            <div className="text-xs text-slate-500">Use the sample CSV to format your data correctly.</div>
          </div>
          <Button variant="outline" onClick={downloadTemplate} data-testid="btn-download-template">
            <Download size={14} className="mr-2" /> Sample CSV
          </Button>
        </div>

        <div className="border-t border-slate-200 pt-4">
          <div className="font-medium text-slate-900 mb-2">2. Upload file</div>
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            data-testid="bulk-file-input"
          />
          {file ? <div className="text-xs text-slate-500 mt-2">{file.name}</div> : null}

          <Button onClick={submit} className="mt-4 bg-blue-600 hover:bg-blue-700" disabled={busy || !file} data-testid="btn-bulk-upload-submit">
            <Upload size={14} className="mr-2" /> {busy ? "Uploading..." : "Upload"}
          </Button>
        </div>
      </Card>

      {result ? (
        <Card className="p-5 border-slate-200" data-testid="bulk-result">
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-9 h-9 rounded-md flex items-center justify-center ${result.failed?.length ? "bg-yellow-50 text-yellow-700" : "bg-green-50 text-green-700"}`}>
              {result.failed?.length ? <AlertCircle size={16} /> : <FileCheck2 size={16} />}
            </div>
            <div>
              <div className="font-medium text-slate-900">Import Result</div>
              <div className="text-xs text-slate-500">{result.inserted} inserted · {result.failed?.length || 0} failed · {result.total} total rows</div>
            </div>
          </div>

          {result.failed?.length ? (
            <div className="border border-yellow-200 rounded-md bg-yellow-50 p-3">
              <div className="text-xs font-semibold text-yellow-800 uppercase mb-2">Failed rows</div>
              <ul className="text-xs text-yellow-900 space-y-1 max-h-48 overflow-auto">
                {result.failed.map((f, i) => (
                  <li key={i} className="mono">Row {f.row}: {f.reason}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="mt-4 flex gap-2">
            <Button onClick={() => nav(isPatients ? "/patients" : "/medicines")} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-bulk-done">
              View {isPatients ? "Patients" : "Medicines"}
            </Button>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
